import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { boardsApi, foldersApi } from '@/api/boardsApi'

/**
 * Boards and their cards. Card queries live under the `tickets` root, so the
 * realtime hook (lib/socket.ts) refreshes a board whenever any ticket changes.
 */
export const boardTicketsKey = (id: string) => ['tickets', 'board', id] as const

export function useBoards(enabled = true) {
  return useQuery({ queryKey: ['boards'], queryFn: boardsApi.list, enabled, retry: false })
}

export function useArchivedBoards(enabled = true) {
  return useQuery({ queryKey: ['boards', 'archived'], queryFn: boardsApi.archived, enabled, retry: false })
}

/** My folders and my team's (GET /board-folders). */
export function useBoardFolders(enabled = true) {
  return useQuery({ queryKey: ['board-folders'], queryFn: foldersApi.tree, enabled, retry: false })
}

export function useBoard(id: string | undefined) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: ['boards', id],
    queryFn: async () => {
      const board = await boardsApi.get(id!)
      // Opening a board records it as recent: refresh the list the sidebar reads.
      void queryClient.invalidateQueries({ queryKey: ['boards'], exact: true })
      return board
    },
    enabled: !!id,
    retry: false,
  })
}

export function useBoardTickets(id: string | undefined) {
  return useQuery({ queryKey: boardTicketsKey(id ?? ''), queryFn: () => boardsApi.tickets(id!), enabled: !!id, retry: false })
}

/** Wraps a board or folder write; refreshes the boards, the folders, the board and its cards. */
export function useBoardMutation<A, R>(fn: (args: A) => Promise<R>) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['boards'] })
      void queryClient.invalidateQueries({ queryKey: ['board-folders'] })
      void queryClient.invalidateQueries({ queryKey: ['tickets', 'board'] })
    },
  })
}
